import {createSelector} from 'reselect';
import Sections from '../../../enums/Sections';
import AddressDataValidators from '../../components/address/field-validation/AddressDataValidators';
import PredictionsByField from '../../components/address/field-validation/PredictionsByField';
import statesByField from '../../components/address/field-validation/statesByField';
import getShowBillingAddress from '../BillingAddressSelectors/getShowBillingAddress';
import cityRequiredForDeliveryCountry from '../DeliveryOptionsSelectors/cityRequiredForDeliveryCountrySelector';
import stateRequiredForDeliveryCountrySelector
from '../DeliveryOptionsSelectors/stateRequiredForDeliveryCountrySelector';
import getShouldPassNameCodeForStateIfAvailable from '../getShouldPassNameCodeForStateIfAvailable';
import {getPudoDeliveryData} from '../PudoSelectors';
import {
  getDeliveryType,
  getNewAddress,
  getSections,
  getSelectedAddressID,
  getSelectedDeliveryCountry,
  getSettings,
  shouldCaptureBillingAddress,
} from '../selectors';
import getTaxSubdivisionEnabled from '../getTaxSubdivisionEnabled';

export const isResidentIDRequiredInAddressForm = createSelector(
  [getSelectedDeliveryCountry, getSettings],
  (selectedDeliveryCountry, settings) => {
    const countriesRequireResidentIDInAddressForm = ['BR'];
    
    if (settings?.should_show_resident_id_field_in_address_section_for_delivery_countries?.includes(selectedDeliveryCountry)){
      return true;
    }

    const deliveryCountriesToShowTax = settings.delivery_countries_to_show_tax;
    if (!deliveryCountriesToShowTax) return false;
    
    const taxCountriesUpperCase = deliveryCountriesToShowTax.map(country => country.toUpperCase());
    return countriesRequireResidentIDInAddressForm.includes(selectedDeliveryCountry)
      && taxCountriesUpperCase.includes(selectedDeliveryCountry);
  },
);

export const isPostCodeExemptDeliveryCountry = createSelector(
  [getNewAddress],
  (address) => {
    const postcodeExemptCountries = ['AE', 'IE', 'HK'];
    return postcodeExemptCountries.indexOf(address.country) > -1;
  },
);

export const isPostCodeExemptForSections = createSelector(
  [getSections],
  sections => !sections.includes(Sections.ADDRESS) && (sections.includes(Sections.COLLECTION)
    || sections.includes(Sections.DELIVERY)),
);

export const isPostCodeExempt = createSelector(
  [isPostCodeExemptDeliveryCountry, isPostCodeExemptForSections],
  (isPostCodeExemptByCountry, isPostCodeExemptBySection) => isPostCodeExemptByCountry
    || isPostCodeExemptBySection,
);

export const getResidentialDeliveryData = (state, addressDefinitions) => {
  const addressId = getSelectedAddressID(state);
  
  if (addressId) {
    return {addressId};
  }
  const newAddress = getNewAddress(state);
  if (!newAddress) {
    return;
  }
  
  const {country} = newAddress;
  
  const validExemptPostCode = isPostCodeExempt(state) && newAddress.postCode
    ? AddressDataValidators.validatePostCode(addressDefinitions, newAddress.postCode)
    : isPostCodeExempt(state);
  
  const postcodeIsValid = validExemptPostCode
    || (newAddress.postCode && AddressDataValidators.validatePostCode(addressDefinitions, newAddress.postCode));
  
  const requiresTownCity = cityRequiredForDeliveryCountry(state);
  
  const shouldPassNameCodeForStateIfAvailable = getShouldPassNameCodeForStateIfAvailable(state);
  
  const taxableCountries = getSettings(state).delivery_countries_to_show_tax || [];
  const requiresState = taxableCountries.includes(country) || stateRequiredForDeliveryCountrySelector(state) || getTaxSubdivisionEnabled(state);
  
  const selectedState = newAddress.county;
  let nameCodeToSet = '';
  if (shouldPassNameCodeForStateIfAvailable) {
    const states = statesByField.county(newAddress.country);
    const stateFound = states?.find(field => field.name === selectedState);
    if (stateFound) {
      nameCodeToSet = stateFound.nameCode;
    }
  }
  const county = (shouldPassNameCodeForStateIfAvailable && nameCodeToSet) ? nameCodeToSet : newAddress.county;
  let stateAutocompleteValidation = true;
  const validationStateList = PredictionsByField.county(newAddress.country);
  if (validationStateList !== []) {
    const stateAutocompleteValidationRequired = getSettings(state).should_use_autocomplete_for_state && (statesByField.county(newAddress.country) !== []);
    stateAutocompleteValidation = (stateAutocompleteValidationRequired) ? validationStateList.includes(selectedState) : true;
  }
  
  if (postcodeIsValid && (newAddress.townCity || !requiresTownCity) && (newAddress.county || !requiresState) && stateAutocompleteValidation) {
    return {
      country,
      postCode: newAddress.postCode || '',
      state: (requiresState && AddressDataValidators.validateState(addressDefinitions, newAddress.county)
        && county) || '',
      townCity: (requiresTownCity && AddressDataValidators.validateTownCity(addressDefinitions, newAddress.townCity)
        && newAddress.townCity) || '',
    };
  }
};

export const getSelectedDeliveryData = (state, addressDefinitions = []) => {
  const deliveryType = getDeliveryType(state);
  if (deliveryType === 'pudo') {
    return getPudoDeliveryData(state);
  }
  return getResidentialDeliveryData(state, addressDefinitions);
};

export const isUsingDifferentBillingAddress = createSelector(
  [getShowBillingAddress, shouldCaptureBillingAddress],
  (showBillingAddress, shouldCapture) => shouldCapture || showBillingAddress,
);
